// src/redux/slices/profile.ts
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  ApiApplicationInformation,
  ApiConsentState,
  ApiConsentStateItem,
  ApiPostSearchOrderByRequest,
  ApiScreenConfig,
  ApiUnit,
  ApiUserProfile,
  GenericHttpResult,
} from '@agunity/api-v4';

import { RootState } from '../store';
import { SUCCESS_STATUS } from '../../Constants';
import { apiService } from '../../api/apiService';
import { RenderTree } from '../../stories/Utils/TreeUtils';

import { parseJSON } from '../../utils';
import { ThemeColors } from '../../stories/Styles/AgColor';

export interface ExtendedRenderTree extends RenderTree {
  hasCh?: boolean;
}
export interface GlobalState {
  // [GA]: active user you will get out of the apiService.userToken
  users: ApiUserProfile[] | null;
  identityId: number | null;

  // requested user page (for forwarding after login / user select)
  requestedPath: string | null;
  errorMessage: string | null;
  apiErrorMessage: string | null;

  orgTree: ExtendedRenderTree | null;
  version: ApiApplicationInformation;

  units: ApiUnit[];

  consents: ApiConsentStateItem[];
  activeConsent: number;
  themeColors: ThemeColors | null;
  onlyShowMF: boolean;
}

const initialState: GlobalState = {
  users: null,
  identityId: null,
  requestedPath: null,
  errorMessage: null,
  apiErrorMessage: null,
  orgTree: null,
  version: {},
  units: [],
  consents: [],
  activeConsent: 0,
  themeColors: null,
  onlyShowMF: false,
};

export const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    store: (
      state: GlobalState,
      action: PayloadAction<{
        users?: ApiUserProfile[] | null;
        identityId?: number | null;
        requestedPath?: string | null;
      }>
    ) => {
      // Copy each property from the payload to the state
      Object.assign(state, action.payload);
    },
    clear: (state: GlobalState) => {
      // Set each property of the state back to its initial value
      Object.assign(state, initialState);
    },
    setErrorMessage: (
      state: GlobalState,
      action: PayloadAction<string | null>
    ) => {
      state.errorMessage = action.payload;
    },
    setApiErrorMessage: (
      state: GlobalState,
      action: PayloadAction<string | null>
    ) => {
      state.apiErrorMessage = action.payload;
    },
    setOrgTree: (
      state: GlobalState,
      action: PayloadAction<ExtendedRenderTree | null>
    ) => {
      state.orgTree = action.payload;
    },
    setVersion: (state, action) => {
      state.version = action.payload;
    },
    setUnits: (state, action: PayloadAction<ApiUnit[]>) => {
      state.units = action.payload;
    },
    setConsents: (state, action: PayloadAction<ApiConsentStateItem[]>) => {
      state.consents = action.payload;
    },
    setActiveConsentIndex: (state, action: PayloadAction<number>) => {
      state.activeConsent = action.payload;
    },
    setThemeColors: (state, action: PayloadAction<ThemeColors | null>) => {
      state.themeColors = action.payload;
    },
    setOnlyShowMF: (state, action: PayloadAction<boolean>) => {
      state.onlyShowMF = action.payload;
    },
  },
});

export const {
  store,
  clear,
  setErrorMessage,
  setApiErrorMessage,
  setOrgTree,
  setVersion,
  setUnits,
  setConsents,
  setActiveConsentIndex,
  setThemeColors,
  setOnlyShowMF,
} = globalSlice.actions;

export const filterUniqueTokenUsers = (
  users: ApiUserProfile[] | null
): ApiUserProfile[] | null => {
  const uniqueTokens = new Set<string>();
  return (
    users?.filter((user: ApiUserProfile) => {
      if (
        !user.token ||
        !user.isActive ||
        user.activeStatus !== 0 ||
        uniqueTokens.has(user.token)
      ) {
        return false;
      }
      uniqueTokens.add(user.token);
      return true;
    }) || null
  );
};

export const selectErrorMessage = (state: RootState) =>
  state.global.errorMessage;
export const selectApiErrorMessage = (state: RootState) =>
  state.global.apiErrorMessage;
export const selectOrgTree = (state: RootState) => state.global.orgTree;
export const selectVersion = (state: RootState) => state.global.version;
export const selectUnits = (state: RootState) => state.global.units;
export const selectConsents = (state: RootState) => state.global.consents;
export const selectActiveConsent = (state: RootState) =>
  state.global.activeConsent;
export const selectThemeColors = (state: RootState) => state.global.themeColors;

const selectUsers = (state: RootState) => state.global.users;

export const selectFilteredUsers = createSelector(selectUsers, (users) => {
  return filterUniqueTokenUsers(users);
});

export const selectMyIdentityId = (state: RootState) => state.global.identityId;
export const selectRequestedPath = (state: RootState) =>
  state.global.requestedPath;

export const getAPIVersion = () => {
  return async (
    dispatch: Function
  ): Promise<GenericHttpResult<ApiApplicationInformation> | void> => {
    const response = await apiService.onlineV11VersionList();
    if (response.status === SUCCESS_STATUS) dispatch(setVersion(response.data));
    return response;
  };
};

export const fetchAllListManagement = (template: number) => async () => {
  return await apiService.searchV10AllColumnsDetail(template);
};

export const saveAllListManagement =
  (data: ApiPostSearchOrderByRequest) => async () => {
    return await apiService.searchV10ColumnOrderCreate(data);
  };

export const getUnitList =
  () => async (dispatch: Function, getState: Function) => {
    const units = selectUnits(getState());
    if ((units || []).length) return;

    const response = await apiService.systemV10UnitsList();
    if (response.data.length) dispatch(setUnits(response.data || []));
  };

export const getConsentActiveList = (locale: string, force?: boolean) => {
  return async (dispatch: Function, getState: Function) => {
    const consents = selectConsents(getState());
    if ((consents || []).length && !force) return;

    const response = await apiService.consentV11ActiveListDetail(locale);
    if (response.data.length) dispatch(setConsents(response.data || []));
    return response;
  };
};

export const patchConsentItemState = (
  consentStateId: number,
  state: ApiConsentState
) => {
  return async (): Promise<GenericHttpResult<void>> => {
    const response = await apiService.consentV10ItemPartialUpdate(
      consentStateId,
      state
    );
    return response;
  };
};

export const getScreenConfig = () => {
  return async (
    dispatch: Function
  ): Promise<GenericHttpResult<ApiScreenConfig>> => {
    const response = await apiService.uiV10ScreenConfigList();
    if (response.status === SUCCESS_STATUS && response.data) {
      const parsedThemeColors = parseJSON(response.data?.colorTheme || '');
      dispatch(setThemeColors(parsedThemeColors || null));
      dispatch(setOnlyShowMF(!!response.data?.onlyShowMF));
    }
    return response;
  };
};

export const globalInit = (locale: string) => async (dispatch: Function) => {
  await dispatch(getConsentActiveList(locale));
  await dispatch(getUnitList());
  await dispatch(getScreenConfig());
};

export default globalSlice.reducer;
